import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "../../config/api";
import { errorMessage } from "../../utils/errorMessage";

/**
 * Brief 14g — Panel D: FWRA Aggregations.
 *
 * COLP-only in v1.  Renders 8 tile-cards (one per aggregation A1–A8)
 * + a single "Generate Monthly CSV Export" button that streams a ZIP
 * from /compliance/exports/monthly-csv and triggers a browser download.
 *
 * BM-H620 (26 Sep 2026): the panel never shows a number it was not given.
 * Until then every tile read "0" or "0%" when the request failed (it always
 * did: the routes were not mounted on CaseMinder), and the measures that are
 * not built (A2, A6, A8) showed zeros under a small "stubbed" badge. Now a
 * failed load reads "Not available" and a measure the server reports as
 * measured: false reads "Not built", with the server's reason.
 */
// BM-H620: a missing value shows "—", never 0.
const pct = (v) => (typeof v === "number" ? `${Math.round(v * 100)}%` : "—");

const AGGREGATIONS = [
  { id: "a1", title: "Matters by Risk Band", path: "matters-by-risk-band", headline: (d) => d?.total ?? "—" },
  { id: "a2", title: "AML Profile Completion", path: "aml-profile-completion", headline: (d) => pct(d?.completion_rate) },
  { id: "a3", title: "SoF Depth Distribution", path: "sof-depth", headline: (d) => d?.compliance_flag ?? "—" },
  { id: "a4", title: "Override Usage", path: "overrides", headline: (d) => d?.aml_profile_overrides?.total ?? "—" },
  { id: "a5", title: "File Review Coverage", path: "file-review-coverage", headline: (d) => pct(d?.coverage_rate) },
  { id: "a6", title: "CDD Currency", path: "cdd-currency", headline: (d) => d?.overdue ?? "—" },
  { id: "a7", title: "Top Risk Factors", path: "top-risk-factors", headline: (d) => d?.top_5?.length ?? "—" },
  { id: "a8", title: "Training Currency", path: "training-currency", headline: (d) => d?.status ?? "—" },
];

export default function FWRAAggregationsPanel({ authHeaders }) {
  const [payloads, setPayloads] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    setPayloads({});
    try {
      // BM-H620: one call for all eight measures.
      const res = await axios.get(`${API}/compliance/aggregations`, {
        headers: authHeaders(),
      });
      setPayloads(res.data?.aggregations || {});
    } catch (err) {
      // nosilent: surface error on panel; log once for ops visibility.
      // eslint-disable-next-line no-console
      console.warn("FWRAAggregationsPanel: fetch failed", err);
      setError(errorMessage(err, "The FWRA measures could not be loaded."));
    } finally {
      setLoading(false);
    }
  }, [authHeaders]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const exportMonthly = useCallback(async () => {
    setExporting(true);
    try {
      const res = await axios.get(
        `${API}/compliance/exports/monthly-csv`,
        { headers: authHeaders(), responseType: "blob" }
      );
      const period = new Date().toISOString().slice(0, 7);
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = blobUrl;
      link.setAttribute("download", `bonsai-fwra-monthly-${period}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      // nosilent: surface error toast via console + setError; never silent.
      // eslint-disable-next-line no-console
      console.warn("FWRAAggregationsPanel: csv export failed", err);
      setError(errorMessage(err, "csv export failed"));
    } finally {
      setExporting(false);
    }
  }, [authHeaders]);

  return (
    <section
      className="rounded-xl border border-white/[0.07] bg-navy-card p-4"
      data-testid="fwra-aggregations-panel"
    >
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-semibold text-slate-100">
          Panel D — FWRA Aggregations
        </h2>
        <button
          type="button"
          onClick={exportMonthly}
          disabled={exporting || loading}
          className="px-3 py-1 text-sm bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 rounded text-white"
          data-testid="fwra-export-monthly-csv-btn"
        >
          {exporting ? "Generating…" : "Generate Monthly CSV Export"}
        </button>
      </div>

      {error && (
        <div
          className="mb-3 text-xs text-rose-300"
          data-testid="fwra-panel-error"
        >
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {AGGREGATIONS.map((agg) => {
          const payload = payloads[agg.id];
          const notBuilt = payload?.measured === false;
          let headline;
          if (loading) headline = "…";
          else if (!payload) headline = "Not available";
          else if (notBuilt) headline = "Not built";
          else headline = String(agg.headline(payload.data || {}));
          return (
            <div
              key={agg.id}
              className="rounded border border-slate-700 bg-slate-950/40 p-3"
              data-testid={`fwra-tile-${agg.id}`}
            >
              <div className="flex items-center justify-between">
                <div className="text-xs font-medium text-slate-300 uppercase tracking-wide">
                  {agg.id.toUpperCase()} — {agg.title}
                </div>
                {notBuilt && (
                  <span
                    className="text-[10px] px-1.5 py-0.5 bg-amber-900/40 border border-amber-700 text-amber-200 rounded"
                    data-testid={`fwra-not-built-badge-${agg.id}`}
                  >
                    not built
                  </span>
                )}
              </div>
              <div
                className="mt-1 text-lg font-semibold text-slate-100"
                data-testid={`fwra-headline-${agg.id}`}
              >
                {headline}
              </div>
              {notBuilt && payload?.metadata?.reason && (
                <div
                  className="mt-1 text-xs text-slate-400"
                  data-testid={`fwra-not-built-reason-${agg.id}`}
                >
                  {payload.metadata.reason}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}
